import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { FloatingInput } from '@/components/ui/floating-input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { apiFetch, signInWithToken } from '@/utils'
import { useUser } from '@/hooks/use-user'
import { EyeOff, Eye, Loader2, Mail, ShieldCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import logo from '@/assets/logo-login.svg';

const loginFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters')
})

type LoginFormData = z.infer<typeof loginFormSchema>

type MfaFactor = {
  enrollmentId: string
  displayName?: string | null
  factorType?: string | null
}

type MfaChallenge = {
  email: string
  password: string
  factors: MfaFactor[]
}

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isShowingPassword, setIsShowingPassword] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState('')
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false)

  const [, navigate] = useLocation()
  const { toast } = useToast()
  const { setUserDataLoading } = useUser()

  const searchParams = new URLSearchParams(window.location.search)
  const [unverifiedEmailState, setUnverifiedEmailState] = useState<string | null>(null)
  const unverifiedEmail = unverifiedEmailState ?? searchParams.get('unverified')

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return
    setIsResending(true)
    try {
      const response = await apiFetch('/v1/users/send-verification', {
        method: 'POST',
        body: JSON.stringify({ email: unverifiedEmail })
      })
      const result = await response.json()
      if (result.success && result.data?.alreadyVerified) {
        toast({
          title: 'Already verified',
          description: 'Your email is already verified. Please log in.'
        })
        navigate('/login', { replace: true })
      } else if (result.success) {
        toast({
          title: 'Verification email sent',
          description: 'Please check your inbox for the verification link.'
        })
      } else {
        throw new Error(result.error?.message || 'Failed to send verification email')
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send verification email',
        variant: 'destructive'
      })
    } finally {
      setIsResending(false)
    }
  }
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const completeSuccessfulLogin = async (token: string) => {
    await signInWithToken(token)
    setUserDataLoading(true)
    toast({
      title: 'Success',
      description: 'Logged in successfully'
    })
  }

  const submitLogin = async (
    email: string,
    password: string,
    totpCode?: string
  ): Promise<{ kind: 'success' } | { kind: 'mfa'; factors: MfaFactor[] } | { kind: 'unverified' } | { kind: 'error'; message: string }> => {
    const loginResponse = await apiFetch('/v1/users/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        ...(totpCode ? { totpCode } : {})
      })
    })

    const { data: loginResult, error } = await loginResponse.json()
    const errorMessage = typeof error?.message === 'string' ? error.message : ''

    if (
      loginResponse.status === 403 &&
      errorMessage.toLowerCase().includes('email not verified')
    ) {
      return { kind: 'unverified' }
    }

    if (loginResponse.ok && loginResult?.mfaRequired) {
      const factors: MfaFactor[] = Array.isArray(loginResult.mfaInfo) ? loginResult.mfaInfo : []
      return { kind: 'mfa', factors }
    }

    if (!loginResponse.ok || !loginResult?.token) {
      return { kind: 'error', message: errorMessage || 'Sign in failed' }
    }

    await completeSuccessfulLogin(loginResult.token)
    return { kind: 'success' }
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      const result = await submitLogin(data.email, data.password)
      if (result.kind === 'unverified') {
        setUnverifiedEmailState(data.email)
        toast({
          title: 'Verify your email',
          description: `We sent a new verification link to ${data.email}.`
        })
        return
      }
      if (result.kind === 'mfa') {
        setMfaChallenge({ email: data.email, password: data.password, factors: result.factors })
        setMfaCode('')
        setMfaError('')
        return
      }
      if (result.kind === 'error') {
        throw new Error(result.message)
      }
    } catch (error) {
      console.error(error)
      toast({
        title: 'Sign in failed',
        description: error instanceof Error ? error.message : 'Unknown reason',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const cancelMfaChallenge = () => {
    setMfaChallenge(null)
    setMfaCode('')
    setMfaError('')
  }

  const onSubmitMfa = async () => {
    if (!mfaChallenge) return
    const code = mfaCode.replace(/\s+/g, '')
    if (code.length !== 6) {
      setMfaError('Enter the 6-digit code from your authenticator app.')
      return
    }
    setIsVerifyingMfa(true)
    setMfaError('')
    try {
      const result = await submitLogin(mfaChallenge.email, mfaChallenge.password, code)
      if (result.kind === 'mfa') {
        // BE asked for MFA again (e.g., wrong code on a fresh pending credential).
        setMfaError('That code didn’t match. Please try again.')
        return
      }
      if (result.kind === 'unverified') {
        cancelMfaChallenge()
        setUnverifiedEmailState(mfaChallenge.email)
        toast({
          title: 'Verify your email',
          description: `We sent a new verification link to ${mfaChallenge.email}.`
        })
        return
      }
      if (result.kind === 'error') {
        setMfaError(result.message)
        return
      }
      // Success — keep the challenge state cleared; UserProvider will navigate.
      cancelMfaChallenge()
    } catch (error) {
      console.error(error)
      setMfaError(error instanceof Error ? error.message : 'Unable to verify your code. Please try again.')
    } finally {
      setIsVerifyingMfa(false)
    }
  }

  return (
    <div className="flex justify-center items-center">
      <div className="min-h-auto h-full flex items-end justify-between login-page flex-col relative w-full max-w-5xl">
        <div className="flex flex-col justify-center items-center w-full h-[calc(100vh-414px)]">
          <img src={logo} alt="" />
          <p className="text-[20px] leading-6 font-bold text-[#ffffff] font-montserrat mt-1 mb-1">SECH</p>
          <p className="text-xs leading-[14px] font-montserrat font-medium text-[#FFFFFF]">Digital Wallet</p>
        </div>
        <div className="max-w-[1024px] w-full">
          <Card className="bg-[#2F2F32] border-0  p-6 rounded-tl-[36px] rounded-tr-[36px] rounded-b-none">
            <div className="text-center mb-6">
              <h1 className="text-[20px] leading-[26px] font-semibold text-[#ffffff] font-figtree">Welcome to Secrets Hideaway</h1>
            </div>

            {mfaChallenge ? (
              <div>
                <div className="mb-6 p-4 rounded-[8px] bg-[#212124] text-center">
                  <ShieldCheck className="h-6 w-6 text-[#EF4444] mx-auto mb-2" />
                  <p className="text-sm text-[#ffffff] font-figtree mb-1">
                    Two-factor authentication required
                  </p>
                  <p className="text-xs text-[#AFAFAF] font-figtree">
                    Enter the 6-digit code from your authenticator app to finish signing in.
                  </p>
                </div>

                <div className="mb-3">
                  <Label className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">
                    6-digit code
                  </Label>
                  <Input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value.replace(/[^0-9]/g, ''))
                      setMfaError('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        onSubmitMfa()
                      }
                    }}
                    className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)] tracking-[0.4em] text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B6565] focus-visible:ring-offset-0 text-[#ffffff]"
                  />
                  {mfaError && (
                    <p className="text-xs text-[#EF4444] mt-2 text-center font-figtree">{mfaError}</p>
                  )}
                </div>

                <Button
                  type="button"
                  onClick={onSubmitMfa}
                  disabled={isVerifyingMfa}
                  className="w-full h-[42px] text-sm leading-[14px] font-medium font-figtree mb-3 text-[#ffffff] rounded-[21px] bg-[#EF4444]"
                >
                  {isVerifyingMfa ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify and sign in'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="link"
                  onClick={cancelMfaChallenge}
                  className="w-full h-auto text-xs font-regular text-[#AFAFAF] leading-[14px] font-figtree"
                >
                  Use a different account
                </Button>
              </div>
            ) : (
              <>
            {unverifiedEmail && (
              <div className="mb-6 p-4 rounded-[8px] bg-[#212124] text-center">
                <Mail className="h-6 w-6 text-[#EF4444] mx-auto mb-2" />
                <p className="text-sm text-[#ffffff] font-figtree mb-1">
                  Verify your email to sign in
                </p>
                <p className="text-xs text-[#AFAFAF] font-figtree mb-3">
                  We sent a verification link to {unverifiedEmail}
                </p>
                <Button
                  type="button"
                  className="w-full h-[38px] text-sm font-medium rounded-[21px] bg-[#EF4444]"
                  disabled={isResending}
                  onClick={handleResendVerification}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <Label className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">
                        Email
                      </Label>

                      <FormControl>
                        <Input
                          autoFocus
                          {...field}
                          type="email"
                          autoComplete="email"
                          placeholder="Enter your email"
                          className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]  focus-visible:outline-none
                                      focus-visible:ring-2
                                      focus-visible:ring-[#6B6565]
                                      focus-visible:ring-offset-0 text-[#ffffff]"
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="mb-3">
                      <Label className="text-[13px] leading-4 font-medium font-figtree text-[#ffffff] mb-2">
                        Password
                      </Label>

                      <div className="relative">
                        <FormControl>
                          <Input
                            {...field}
                            type={isShowingPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            className="bg-[#212124] border-0 h-[42px] rounded-[8px] text-[#ffffff]  mx-1 w-[calc(100%-8px)] focus-visible:outline-none
                                        focus-visible:ring-2
                                        focus-visible:ring-[#6B6565]
                                        focus-visible:ring-offset-0"
                          />
                        </FormControl>

                        <button
                          type="button"
                          onClick={() => setIsShowingPassword(prev => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-gray-500"
                          aria-label={isShowingPassword ? "Hide password" : "Show password"}
                        >
                          {isShowingPassword ? (
                            <Eye className="h-5 w-5"  />
                          ) : (
                            <EyeOff className="h-5 w-5" />
                          )}
                        </button>
                      </div>

                      <FormMessage />
                    </FormItem>


                  )}
                />

                <div className="w-full h-auto flex justify-between mb-9">
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 h-auto text-xs font-regular text-[#AFAFAF] leading-[14px] font-figtree"
                    onClick={() => navigate('/resend-verification')}>
                    Resend verification
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 h-auto text-xs font-regular text-[#AFAFAF] leading-[14px] font-figtree"
                    onClick={() => navigate('/forgot-password')}>
                    Forgot password?
                  </Button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-[42px] text-sm leading-[14px] font-medium font-figtree mb-6 text-[#ffffff] rounded-[21px] bg-[#EF4444]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>

              </form>
            </Form>


            <div className="text-center flex flex-col items-center">
              <p className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF]">
                Don't have an account?{' '}
                <Button
                  variant="link"
                  className="p-0 h-auto font-semibold font-figtree text-xs leading-4 text-[#EF4444] hover:text-[#DC2626]"
                  onClick={() => navigate('/signup')}>
                  Sign up
                </Button>
              </p>


            </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
